/**
 * Une photo de téléphone pèse 3 à 8 Mo. Sur un chantier en zone blanche, au
 * fond d'un hangar ou à la Réunion en 3G, c'est la différence entre un envoi
 * qui passe et un envoi qui n'arrive jamais. On réduit à 1600 px de côté en
 * JPEG : largement assez pour lire un raccordement ou une plaque signalétique.
 */
export const COTE_MAX = 1600;
const QUALITE = 0.82;

/** Les dimensions réduites, proportions gardées ; inchangées si déjà petites. */
export function dimensionsCible(
  largeur: number,
  hauteur: number,
  max = COTE_MAX
): { largeur: number; hauteur: number } {
  const plusGrand = Math.max(largeur, hauteur);
  if (plusGrand <= max) return { largeur, hauteur };
  const ratio = max / plusGrand;
  return { largeur: Math.round(largeur * ratio), hauteur: Math.round(hauteur * ratio) };
}

/** Rend la photo réduite, ou l'originale si le navigateur ne sait pas faire (ou si ce n'est pas une image). */
export async function compresserPhoto(fichier: File, cote = COTE_MAX): Promise<File> {
  if (!fichier.type.startsWith('image/') || fichier.type === 'image/svg+xml') return fichier;
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') return fichier;
  try {
    const image = await createImageBitmap(fichier, { imageOrientation: 'from-image' });
    const { largeur, hauteur } = dimensionsCible(image.width, image.height, cote);
    if (largeur === image.width && fichier.size < 900_000) {
      image.close();
      return fichier;
    }
    const toile = document.createElement('canvas');
    toile.width = largeur;
    toile.height = hauteur;
    const contexte = toile.getContext('2d');
    if (!contexte) return fichier;
    contexte.drawImage(image, 0, 0, largeur, hauteur);
    image.close();
    const blob = await new Promise<Blob | null>((resolve) => {
      toile.toBlob(resolve, 'image/jpeg', QUALITE);
    });
    if (!blob || blob.size >= fichier.size) return fichier;
    const nom = fichier.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], nom, { type: 'image/jpeg', lastModified: fichier.lastModified });
  } catch {
    return fichier;
  }
}
